(function (root, factory) {
  const api = factory(typeof module === 'object' && module.exports ? require('./free-gift-rules.js') : root.OtelloGiftRules);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.OtelloGiftCart = api;
})(typeof globalThis === 'object' ? globalThis : this, function (rules) {
  'use strict';
  const productId = '15947891605888';
  const variantIds = ["59889254236544","59889254269312","59889254302080","59889254334848","59889254367616","59889254400384","59889254433152","59889254465920","59889254498688","59889254531456","59889254564224","59889254596992","59889254629760","59889254662528","59889254695296","59889254728064","59889254760832","59889254793600","59889254826368","59889254859136","59889254891904","59889254924672","59889254957440","59889254990208","59889255022976","59889255055744"];
  const variants = Object.freeze(Object.fromEntries(variantIds.map((id, i) => [(50 + i * 10) * 100, id])));
  const sourceProperty = '_oh_gift_source';
  const id = value => String(value || '').split('/').pop();

  function promotional(line) {
    return id(line.productId) === productId || variantIds.includes(id(line.variantId)) || rules.promotional(line);
  }
  const fields = Object.freeze({ kind: 'Free gift', amount: 'Gift card amount (GBP)', variant: 'Gift qualifying variant ID',
    item: 'Gift qualifying item', sku: 'Gift qualifying SKU', reference: 'Gift source reference', status: 'Gift status',
    terms: 'Gift terms version', issuance: 'Gift card issuance', validity: 'Gift card validity' });
  const version = 'order-attributes-v1';
  const kinds = { card: 'Gift card', delivery: 'Complimentary White Glove Delivery' };
  function reference(line) {
    const properties = Object.entries(line.properties || {}).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0);
    return encodeURIComponent(JSON.stringify([id(line.productId), id(line.variantId), String(line.sellingPlanId || ''), properties]))
      .replace(/[!'()*]/g, character => '%' + character.charCodeAt(0).toString(16).toUpperCase());
  }
  function hasRequest(cart) { return Object.values(fields).some(key => Boolean(cart?.attributes?.[key])); }
  function choiceFrom(cart) {
    const attributes = cart?.attributes || {};
    const kind = Object.keys(kinds).find(key => kinds[key] === attributes[fields.kind]);
    if (!kind || attributes[fields.terms] !== version) return null;
    const parents = cart.lines.filter(line => !promotional(line) && id(line.variantId) === attributes[fields.variant] && reference(line) === attributes[fields.reference]);
    if (parents.length !== 1) return null;
    const parent = parents[0];
    const value = rules.offer(parent, cart.currency);
    if (!value || (kind === 'delivery' && parent.deliveryExcluded)) return null;
    return { kind, parentKey: parent.key, variantId: id(parent.variantId), faceValuePence: value.faceValuePence };
  }
  function legacyChoice(cart) {
    const gifts = cart.lines.filter(promotional);
    if (gifts.length !== 1) return null;
    const gift = gifts[0];
    const kind = id(gift.productId) === productId ? 'card' : id(gift.variantId) === rules.deliveryVariant ? 'delivery' : null;
    const token = gift.properties?.[sourceProperty];
    const parents = token && cart.lines.filter(line => !promotional(line) && line.properties?.[sourceProperty] === token && id(line.variantId) === gift.properties?._oh_source_variant);
    return kind && parents?.length === 1 ? { kind, parentKey: parents[0].key } : null;
  }
  function desired(cart, choice) {
    const attributes = Object.fromEntries(Object.values(fields).map(key => [key, '']));
    const parent = choice && cart.lines.find(line => line.key === choice.parentKey && !promotional(line));
    const value = parent && rules.offer(parent, cart.currency);
    if (!value || !kinds[choice.kind] || (choice.kind === 'delivery' && parent.deliveryExcluded)) return attributes;
    return { ...attributes, [fields.kind]: kinds[choice.kind], [fields.amount]: choice.kind === 'card' ? String(value.faceValuePence / 100) : '',
      [fields.variant]: id(parent.variantId), [fields.item]: parent.title || id(parent.variantId), [fields.sku]: parent.sku || '',
      [fields.reference]: reference(parent), [fields.status]: 'Pending eligibility review', [fields.terms]: version,
      [fields.issuance]: choice.kind === 'card' ? '30 days after final delivery' : '',
      [fields.validity]: choice.kind === 'card' ? '6 months from issuance' : '' };
  }
  function plan(cart, choice = hasRequest(cart) ? choiceFrom(cart) : legacyChoice(cart)) {
    const attributes = desired(cart, choice);
    const updates = Object.fromEntries(cart.lines.filter(promotional).map(line => [line.key, 0]));
    const keys = attributes[fields.kind] ? Object.values(fields).filter(key => ![fields.item, fields.sku].includes(key)) : Object.values(fields);
    const changed = Object.keys(updates).length > 0 || keys.some(key => (cart.attributes?.[key] || '') !== attributes[key]);
    return { attributes, updates, changed };
  }
  class Controller {
    constructor(transport, onChange = () => {}) {
      this.transport = transport;
      this.onChange = onChange;
      this.cart = null;
      this.tail = Promise.resolve();
      this.busy = false;
      this.pending = null;
    }
    run(task) {
      const next = this.tail.then(async () => {
        this.busy = true;
        try { return await task(); }
        finally { this.busy = false; this.onChange(this.cart, this); }
      });
      this.tail = next.catch(() => {});
      return next;
    }
    async read() {
      const cart = await this.transport.read();
      if (!cart || !Array.isArray(cart.lines) || !cart.attributes || typeof cart.attributes !== 'object' || Array.isArray(cart.attributes) || typeof cart.currency !== 'string') {
        throw new Error('Unable to check your basket. Please refresh the page and try again.');
      }
      this.cart = cart;
      return cart;
    }
    async repair(cart) {
      cart ||= await this.read();
      for (let attempt = 0; attempt < 3; attempt++) {
        const result = plan(cart);
        if (!result.changed) return cart;
        await this.transport.update({ attributes: result.attributes, ...(Object.keys(result.updates).length ? { updates: result.updates } : {}) });
        cart = await this.read();
      }
      throw new Error('Your basket is still changing. Please check your gift selection before checking out.');
    }
    refresh() { return this.run(() => this.repair()); }
    select(kind, parentKey, pendingProduct) {
      if (!parentKey) {
        if (kind && (!kinds[kind] || !pendingProduct || !rules.offer(pendingProduct, this.cart?.currency || 'GBP') || (kind === 'delivery' && pendingProduct.deliveryExcluded))) {
          return Promise.reject(new Error('This item does not qualify for a free gift.'));
        }
        this.pending = kind ? { kind, parentKey: pendingProduct.key, variantId: id(pendingProduct.variantId) } : null;
        this.onChange(this.cart, this);
        return Promise.resolve(this.cart);
      }
      const previousSource = this.cart?.lines.find(line => line.key === parentKey);
      return this.run(async () => {
        const cart = await this.read();
        const parents = cart.lines.filter(line => previousSource ? reference(line) === reference(previousSource) : line.key === parentKey);
        const parent = parents.length === 1 && parents[0];
        const attributes = desired(cart, kind && parent ? { kind, parentKey: parent.key } : null);
        if (kind && !attributes[fields.kind]) throw new Error('This item no longer qualifies for a free gift.');
        this.pending = null;
        const result = plan(cart, kind ? { kind, parentKey: parent.key } : null);
        if (result.changed) await this.transport.update({ attributes, ...(Object.keys(result.updates).length ? { updates: result.updates } : {}) });
        return this.repair(await this.read());
      });
    }
    clear() {
      this.pending = null;
      return this.run(async () => {
        const cart = await this.read();
        const result = plan(cart, null);
        if (result.changed) await this.transport.update({ attributes: result.attributes, ...(Object.keys(result.updates).length ? { updates: result.updates } : {}) });
        return this.read();
      });
    }
    addFurniture(item, product) {
      const pending = this.pending;
      this.pending = null;
      return this.run(async () => {
        const cart = await this.repair();
        const attach = pending && pending.variantId === id(item.id) && rules.offer(product, cart.currency);
        const response = await this.transport.add([item]);
        try {
          const updated = await this.read();
          if (attach) {
            const key = response?.items?.[0]?.key;
            const candidates = updated.lines.filter(line => !promotional(line) && id(line.variantId) === id(item.id) && (!key || line.key === key));
            const attributes = desired(updated, candidates.length === 1 ? { kind: pending.kind, parentKey: candidates[0].key } : null);
            if (!attributes[fields.kind]) throw new Error('This item no longer qualifies for the selected gift.');
            await this.transport.update({ attributes });
            return await this.repair(await this.read());
          }
          return await this.repair(updated);
        } catch (error) {
          throw new Error('Your item was added, but we could not confirm your gift selection. Please check your basket and select your gift again.');
        }
      });
    }
  }
  return { Controller, plan, desired, choiceFrom, hasRequest, reference, fields, kinds, promotional, variants, productId, sourceProperty, termsVersion: version };
});
